import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { GaiaPayloadConfigs } from "@expert/shared-types";
import { Center, Container, Flex } from "@mantine/core";
import { useFocusWithin, useHotkeys } from "@mantine/hooks";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { type JSX, type KeyboardEvent, type SyntheticEvent, useEffect, useRef, useState } from "react";
import { useHandleAutoComplete, useSendExpertAssistMessage } from "../../hooks";
import { useTimelineStore } from "../../state";
import { useAutoscroll } from "../../utils";
import { Autocomplete } from "./Autocomplete";
import classes from "./MessageInput.module.css";
import { SendButton } from "./SendButton";
import { ToolboxButton } from "./ToolboxButton";
import type { ToolboxContext } from "./types";

const formatMessage = (msg: string) => {
    return msg.replace(/\n+$/, ""); // remove all trailing newline characters
};

interface MessageInputProps {
    toolboxContext: ToolboxContext;
    logger: Logger;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
    gaiaPayloadProps: GaiaPayloadConfigs;
}

export function MessageInput({ toolboxContext, logger, scrollRef, gaiaPayloadProps }: MessageInputProps): JSX.Element {
    const localLogger = logger.child({ module: "MessageInput" });

    const { dispatcher } = useReactAnalytics();
    const {
        setNewSuggestions,
        setShouldAutoOpenToolbox,
        setShowAutocompleteSuggestions,
        getTimelineState,
        currentSessionId,
        shouldAutoOpenToolbox,
    } = useTimelineStore();
    const { autoCompleteSuggestions, messages, showAutocompleteSuggestions } = getTimelineState(currentSessionId);

    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const { start: startAutoscroll } = useAutoscroll(scrollRef);

    const [input, setInput] = useState("");
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [previousInput, setPreviousInput] = useState("");
    const [selectedSuggestion, setSelectedSuggestion] = useState("");

    const isSendDisabled = ["", "/"].includes(input.trimEnd());
    const toolboxInput = input.startsWith("/") ? input : undefined;

    // TODO: implement a more robust PII detection mechanism while still avoiding UI lag
    const containsPotentialPii = /(?:\d\D*){4,}/.test(input); // check if input contains 4 or more digits

    const [toolboxOpened, setToolboxOpened] = useState(false);
    const [toolboxTrapFocus, setToolboxTrapFocus] = useState(false);

    const { ref, focused } = useFocusWithin();

    const handleInputChange = (newInputValue: string) => {
        setInput(newInputValue);

        // clear autocomplete suggestions when user deletes all input
        if (newInputValue.trimEnd() === "") {
            setNewSuggestions(currentSessionId, []);
        }

        const shouldOpenToolbox = newInputValue.startsWith("/");
        if (shouldOpenToolbox && !toolboxOpened) {
            void dispatcher.dispatchAction("Shortcut", "OpenToolbox", {});
        }
        setToolboxOpened(shouldOpenToolbox || false);
    };

    const handleSubmit = (event: SyntheticEvent) => {
        event.preventDefault();

        if (selectedSuggestion) {
            void dispatcher.dispatchBusinessEvent("AutoComplete_SuggestionUsed", {
                suggestion: selectedSuggestion,
                sentInput: input,
                previousInput,
                suggestionEdited: input !== selectedSuggestion,
            });
            if (input !== selectedSuggestion) {
                localLogger.info(
                    {
                        previousInput,
                        suggestion: selectedSuggestion,
                        sentInput: input,
                    },
                    "AutoComplete | Expert message sent to model with edited autocomplete suggestion",
                );
            }
            setSelectedSuggestion("");
            setPreviousInput("");
        }

        const type = "Expert";
        const messageId = crypto.randomUUID();
        void dispatcher.dispatchAction("Click", "SendMessage", { messageType: type, messageId });

        const formattedInput = formatMessage(input);
        sendMessage({ text: formattedInput, type, id: messageId, source: "ExpertInput" });

        setInput("");
        setNewSuggestions(currentSessionId, []);

        startAutoscroll();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
        setToolboxTrapFocus((toolboxOpened && ["ArrowDown", "ArrowUp"].includes(event.key)) || false);
    };
    const handleToolboxClose = () => {
        if (toolboxInput) {
            setInput("");
        }
    };

    const { cancel, handleSuggestionClick } = useHandleAutoComplete({
        input,
        logger,
        toolboxInput,
        isSendDisabled,
        setInput,
        setPreviousInput,
        setSelectedSuggestion,
        gaiaPayloadProps,
    });

    // auto-open toolbox on load for visibility to experts on new toolbox feature
    useEffect(() => {
        if (!shouldAutoOpenToolbox) return;
        setToolboxOpened(shouldAutoOpenToolbox);
        setShouldAutoOpenToolbox(false);
    }, [setToolboxOpened, shouldAutoOpenToolbox, setShouldAutoOpenToolbox]);

    useHotkeys(
        [
            [
                "/",
                () => {
                    if (!toolboxOpened) {
                        setToolboxOpened(true);
                        inputRef.current?.focus();
                        setInput(`${input}/`);
                        void dispatcher.dispatchAction("Shortcut", "OpenToolbox", {});
                    }
                },
            ],
        ],
        undefined,
        true,
    );

    useEffect(() => {
        const shouldShow = autoCompleteSuggestions.length > 0 && !!input && focused && !toolboxOpened;
        const didChange = shouldShow !== showAutocompleteSuggestions;
        if (!didChange) return;

        setShowAutocompleteSuggestions(currentSessionId, shouldShow);
        if (shouldShow) {
            const didShowWithFilter = autoCompleteSuggestions.filter((value) => value.includes(input)).length > 0;
            const extraData = { suggestions: autoCompleteSuggestions, didShowWithFilter, input };
            const actionId = "AutoComplete_SuggestionShown";
            localLogger.info(extraData, "AutoComplete | suggestion shown in message input dropdown");
            void dispatcher.dispatchBusinessEvent(actionId, extraData);
            trackFullStoryEvent(actionId, extraData);
        }
    }, [
        autoCompleteSuggestions,
        dispatcher,
        focused,
        input,
        localLogger,
        setShowAutocompleteSuggestions,
        showAutocompleteSuggestions,
        toolboxOpened,
        currentSessionId,
    ]);

    return (
        <Center ref={ref} className={classes.inputContainer}>
            <Container className={classes.inputBackground} />
            <Flex className={classes.inputWrapper} justify="center">
                <Autocomplete
                    className={containsPotentialPii ? "fs-mask" : undefined}
                    data={autoCompleteSuggestions}
                    data-testid="timeline-message-input"
                    dropdownOpened={showAutocompleteSuggestions}
                    handleSendInput={handleSubmit}
                    id="timeline-message-input"
                    isSendDisabled={isSendDisabled}
                    isTextDisabled={messages.length === 0}
                    leftSection={
                        <ToolboxButton
                            opened={toolboxOpened}
                            setOpened={setToolboxOpened}
                            onClose={handleToolboxClose}
                            toolboxContext={toolboxContext}
                            input={toolboxInput}
                            trapFocus={toolboxTrapFocus}
                            logger={logger}
                        />
                    }
                    onBlur={cancel}
                    onKeyDown={handleKeyDown}
                    onOptionSubmit={(option) => {
                        handleSuggestionClick(option);
                    }}
                    onTextareaChange={handleInputChange}
                    ref={inputRef}
                    rightSection={<SendButton isDisabled={isSendDisabled} onClick={handleSubmit} />}
                    value={input}
                />
            </Flex>
        </Center>
    );
}
